import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  useSlotGroup,
  useVibesites,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Matches an empty <p> tag — empty meaning truly empty, whitespace-only,
// or containing only &nbsp; / <br> filler that some richtext editors emit.
const EMPTY_P = String.raw`<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>`;
const LEADING_EMPTY_P_RE = new RegExp(`^(?:\\s*${EMPTY_P}\\s*)+`, 'i');
const TRAILING_EMPTY_P_RE = new RegExp(`(?:\\s*${EMPTY_P}\\s*)+$`, 'i');

function stripEdgeEmptyParagraphs(html: string): string {
  return html.replace(LEADING_EMPTY_P_RE, '').replace(TRAILING_EMPTY_P_RE, '').trim();
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View Amenities CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Amenities',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ============================================================================
// Component
// ============================================================================

export function PetPolicyInfo() {
  const { property } = useVibesites();
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const restrictions = usePropertyField('property_pet_restrictions') as string | null;
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  // Resolve restrictions richtext ourselves so we can strip leading/trailing
  // empty paragraphs (including <p>&nbsp;</p>, <p><br></p>, whitespace-only)
  // that the CSS `p:empty` rule can't catch.
  const restrictionsRaw = useContentToken('petpolicyinfo.restrictions', {
    defaultField: 'property_pet_restrictions',
  });
  const restrictionsHtml = restrictionsRaw
    ? stripEdgeEmptyParagraphs(resolveRichText(restrictionsRaw, property as Record<string, any>))
    : '';

  const ctaLink = useSlotGroup('petpolicyinfo.cta_link', ctaLinkDef);

  const petDepositSublabel = useContentToken('petpolicyinfo.pet_deposit_sublabel');
  const petFeeSublabel = useContentToken('petpolicyinfo.pet_fee_sublabel');

  // Stat values — defaultField in the schema pulls the property value; authors can override per-site.
  const maxPetsValue = useContentToken('petpolicyinfo.max_pets_value');
  const maxWeightValue = useContentToken('petpolicyinfo.max_weight_value');
  const petDepositValue = useContentToken('petpolicyinfo.pet_deposit_value');
  const petFeeValue = useContentToken('petpolicyinfo.pet_fee_value');
  const monthlyRentValue = useContentToken('petpolicyinfo.monthly_rent_value');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);

  // Editable welcome text — authors can override it per-site.
  const welcomeText = useContentToken('petpolicyinfo.welcome_text');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Show the welcome badge if either pet type is allowed by the property
  // OR if an author has explicitly set a custom welcome text.
  const showWelcomeBadge = catsDogsList.length > 0 || Boolean(welcomeText);

  const showMaxPets = hasValue(maxPetsValue);
  const showMaxWeight = hasValue(maxWeightValue);
  const showPetDeposit = hasValue(petDepositValue);
  const showPetFee = hasValue(petFeeValue);
  const showMonthlyRent = hasValue(monthlyRentValue);
  const showStats =
    showMaxPets || showMaxWeight || showPetDeposit || showPetFee || showMonthlyRent;

  const descriptionHtml = policyDescription || policyInfo || null;

  return (
    <section ref={sectionRef} className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {/* Pets welcome badge */}
            {showWelcomeBadge && (
              <div className="flex items-center gap-3 mb-6">
                <PetsAllowedIcon size={28} color="var(--vibe-color-primary)" />
                <RichTextContent
                  path="petpolicyinfo.welcome_text"
                  as="span"
                  className="ppi-hero-title-font text-lg text-black font-medium tracking-wide uppercase"
                />
              </div>
            )}

            {/* Policy description */}
            {descriptionHtml && (
              <RichTextContent
                path="petpolicyinfo.description"
                label="Pet Policy Description"
                defaultField="property_pet_policy_description"
                as="div"
                className="ppi-hero-blurb-font text-[0.95rem] leading-relaxed text-[var(--vibe-color-text)] mb-6 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
              />
            )}

            {/* Pet restrictions */}
            {hasValue(restrictions) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicyinfo.restrictions_heading"
                  as="h3"
                  className="ppi-hero-title-font text-base font-medium text-[var(--vibe-color-text)] mb-3 tracking-wide uppercase"
                />
                <EditableToken
                  path="petpolicyinfo.restrictions"
                  type="richtext"
                  label="Pet Restrictions"
                  defaultField="property_pet_restrictions"
                  as="div"
                  className="ppi-hero-blurb-font text-sm leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_p:empty]:hidden [&_p:empty]:m-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-0.5 [&_a]:text-primary [&_a]:underline"
                >
                  <span
                    dangerouslySetInnerHTML={{ __html: restrictionsHtml }}
                    style={{ display: 'contents' }}
                  />
                </EditableToken>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              <EditableSlotGroup groupPath="petpolicyinfo.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  {ctaLink.label || 'View Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: stat cards */}
          {showStats && (
            <div className="pet-stats">
              {showMaxPets && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicyinfo.max_pets_label" as="span" className="pet-stat-label ppi-hero-title-font" />
                  <EditableToken path="petpolicyinfo.max_pets_value" as="span" className="pet-stat-value ppi-hero-blurb-font">
                    {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
                  </EditableToken>
                </div>
              )}
              {showMaxWeight && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicyinfo.max_weight_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicyinfo.max_weight_value" as="span" className="pet-stat-value">
                    {maxWeightValue ? `${maxWeightValue} lbs` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicyinfo.pet_deposit_label" as="span" className="pet-stat-label ppi-hero-title-font" />
                  <EditableToken path="petpolicyinfo.pet_deposit_value" as="span" className="pet-stat-value ppi-hero-blurb-font">
                    {petDepositValue ? formatCurrency(petDepositValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicyinfo.pet_deposit_sublabel" as="span" className="pet-stat-sublabel ppi-hero-blurb-font">
                    {petDepositSublabel || '(Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showPetFee && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicyinfo.pet_fee_label" as="span" className="pet-stat-label ppi-hero-title-font" />
                  <EditableToken path="petpolicyinfo.pet_fee_value" as="span" className="pet-stat-value ppi-hero-blurb-font">
                    {petFeeValue ? formatCurrency(petFeeValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicyinfo.pet_fee_sublabel" as="span" className="pet-stat-sublabel ppi-hero-blurb-font">
                    {petFeeSublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMonthlyRent && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicyinfo.monthly_rent_label" as="span" className="pet-stat-label ppi-hero-title-font" />
                  <EditableToken path="petpolicyinfo.monthly_rent_value" as="span" className="pet-stat-value ppi-hero-blurb-font">
                    {monthlyRentValue ? `${formatCurrency(monthlyRentValue)}/mo` : ''}
                  </EditableToken>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        /* Match HeroSolidBG's page title (.hero-solid-title-font):
           Oswald at normal weight, applied to the element and all of its
           rich-text children. !important overrides the .pet-stat-label
           weight and any Tailwind font-* utilities on the same element. */
        .ppi-hero-title-font,
        .ppi-hero-title-font * {
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif !important;
          font-weight: 400 !important;
        }

        /* Match HeroSolidBG's blurb (.hero-solid-blurb-font): Space Mono at
           normal weight/style, applied to the element and all of its rich-text
           children. !important overrides .pet-stat-value/.pet-stat-sublabel
           and any Tailwind font-* utilities on the same element. */
        .ppi-hero-blurb-font,
        .ppi-hero-blurb-font * {
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace !important;
          font-weight: 400 !important;
          font-style: normal !important;
        }

        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .pet-content {
            order: 1;
          }
        }

        .pet-stats {
          order: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .pet-stats {
            order: 2;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8ea;
          background: #f7f7f8;
          border-radius: 4px;
          min-height: 100px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin-bottom: 0.5rem;
        }

        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
        }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          background: #f7f7f8;
          border-left: 3px solid var(--vibe-color-primary);
          margin-bottom: 0.5rem;
        }

        /* Font + color styles matched to FloorplansList's feature CTA:
           Oswald at normal weight, filled with the secondary color, and
           hovering to a white background with secondary-color text. */
        .pet-cta {
          display: inline-block;
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
          font-size: 0.8rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-text-on-secondary);
          background: var(--vibe-color-secondary);
          border: 2px solid var(--vibe-color-secondary);
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
        }
        .pet-cta:hover {
          background: #fff;
          color: var(--vibe-color-secondary);
          border-color: var(--vibe-color-secondary);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyInfo',
  displayName: 'Pet Policy Info',
  tokens: {
    'petpolicyinfo.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description: 'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicyinfo.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicyinfo.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicyinfo.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
    },
    'petpolicyinfo.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicyinfo.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicyinfo.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicyinfo.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      description:
        'Maximum pet weight in lbs. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicyinfo.pet_deposit_label': {
      label: 'Pet Deposit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicyinfo.pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      defaultField: 'property_pet_deposit',
      description:
        'Pet deposit amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicyinfo.pet_deposit_sublabel': {
      label: 'Pet Deposit Sublabel',
      type: 'text',
      defaultValue: '(Refundable)',
    },
    'petpolicyinfo.pet_fee_label': {
      label: 'Pet Fee Label',
      type: 'richtext',
      defaultValue: '<p>Pet Fee</p>',
    },
    'petpolicyinfo.pet_fee_value': {
      label: 'Pet Fee Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      description:
        'Upfront pet fee amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicyinfo.pet_fee_sublabel': {
      label: 'Pet Fee Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
    'petpolicyinfo.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicyinfo.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      description:
        'Monthly pet rent amount. Pulled from the property automatically; override here to display a custom value.',
    },
  },
  slotGroups: {
    'petpolicyinfo.cta_link': ctaLinkDef,
  },
};

(PetPolicyInfo as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicyInfo;
